import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  displayToolName,
  isClientToolName,
} from "@/shared/domain/agentThread/clientTools/ClientTools";
import { describeToolCall } from "@/shared/domain/agentThread/describeToolCall";
import { withoutServerInjectedFields } from "@/shared/domain/agentThread/toolInput";
import {
  BuildIcon,
  ChevronRightIcon,
  ExpandMoreIcon,
  LaptopMacIcon,
} from "@/shared/foundation/icons/mui";

import { type ToolCallStatus } from "../liveToolCalls";

import styles from "./AssistantToolUseContent.module.css";
import { RunPythonCard, RunPythonLiveBadge } from "./RunPythonCard";
import { ToolCallStatusBadge } from "./ToolCallStatusBadge";
import { ToolCallInput } from "./ToolDetailPanel";

interface AssistantToolUseContentProps {
  toolUse: AgentThreadToolUseContent;
  status: ToolCallStatus;
  /** The thread's activity, shown as a running call's status (see `ToolCallStatusBadge`). */
  activity?: string | null;
}

/**
 * A tool call from the moment the model makes it until its result arrives: one pill naming the
 * tool and its status. Clicking the label opens what the call runs: a run_python call opens to
 * the card it shows once finished, and any other call to its arguments.
 */
const AssistantToolUseContent = ({
  toolUse,
  status,
  activity,
}: AssistantToolUseContentProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isClient = isClientToolName(toolUse.tool_name);
  const isRunPython = toolUse.tool_name === "run_python";
  const Icon = isClient ? LaptopMacIcon : BuildIcon;
  const input = withoutServerInjectedFields(toolUse.input);
  const displayName = describeToolCall(
    displayToolName(toolUse.tool_name),
    toolUse.input,
  );

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.call}>
        <div className={styles.messageContainer}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setIsExpanded((open) => !open)}
            aria-expanded={isExpanded}
            aria-label={`Toggle tool details for ${displayName}`}
          >
            <Icon
              className={isClient ? styles.clientToolIcon : styles.buildIcon}
            />
            <RobotoTypography component="span" className={styles.text}>
              {isRunPython ? (
                <b>Running Python…</b>
              ) : (
                <>
                  Using {isClient ? "Client " : ""}Tool <b>{displayName}</b>
                </>
              )}
            </RobotoTypography>
            {isExpanded ? (
              <ExpandMoreIcon className={styles.chevronIcon} />
            ) : (
              <ChevronRightIcon className={styles.chevronIcon} />
            )}
          </button>
          {isRunPython ? (
            <RunPythonLiveBadge status={status} activity={activity} />
          ) : (
            <ToolCallStatusBadge status={status} activity={activity} />
          )}
        </div>
        {isExpanded && (
          <div className={styles.details}>
            {isRunPython ? (
              <RunPythonCard
                toolUseId={toolUse.tool_use_id}
                input={input}
                state={{ kind: "live", status, activity }}
              />
            ) : (
              <div className={styles.arguments}>
                <ToolCallInput toolName={toolUse.tool_name} input={input} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { AssistantToolUseContent };
